import { useEffect, useState } from "react";
import api, { monthLabel } from "@/lib/api";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Line, Bar, ComposedChart } from "recharts";
import { YEAR_COLORS } from "@/components/dashboard/TrendsSection";
import { Badge } from "@/components/ui/badge";
import { exportCSV, exportChartPNG } from "@/lib/exports";
import { ExportBtn } from "@/components/dashboard/ExportBtn";

const tooltipStyle = { backgroundColor: "#111827", border: "1px solid #334155", borderRadius: 8, color: "#F8FAFC", fontSize: 12 };

export const PurchaseAnalysis = ({ fq }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/analysis/purchases${fq ? `?filters=${fq}` : ""}`).then((r) => setData(r.data)).catch(() => {}); }, [fq]);
  if (!data) return <div className="h-96 bg-[#1E293B] border border-[#334155] rounded-lg animate-pulse" />;
  const pvs = data.purchase_vs_sales.map((m) => ({ ...m, label: monthLabel(m.month) }));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div data-testid="purchase-stacked-chart" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
          <h3 className="text-lg font-semibold mb-1 flex items-center justify-between" style={{ fontFamily: "Outfit, sans-serif" }}>
            <span>Purchases from Bajaj — All Years <span className="text-sm text-slate-400 font-normal">(stacked)</span></span>
            <ExportBtn onCSV={() => exportCSV(data.stacked, "purchases-all-years")} onPNG={() => exportChartPNG("purchase-stacked-chart", "purchases-all-years")} />
          </h3>
          <p className="text-xs text-slate-400 mb-3">How much you bought in each month, every year — spot the seasonal buying pattern.</p>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={data.stacked}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" tick={{ fill: "#94A3B8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#334155", opacity: 0.3 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {data.years.map((y, i) => (
                <Bar key={y} dataKey={y} stackId="p" fill={YEAR_COLORS[i % YEAR_COLORS.length]} name={y} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div data-testid="purchase-vs-sales-chart" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
          <h3 className="text-lg font-semibold mb-1 flex items-center justify-between" style={{ fontFamily: "Outfit, sans-serif" }}>
            <span>Purchased vs Sold <span className="text-sm text-slate-400 font-normal">(last 13 months)</span></span>
            <ExportBtn onCSV={() => exportCSV(pvs, "purchased-vs-sold")} onPNG={() => exportChartPNG("purchase-vs-sales-chart", "purchased-vs-sold")} />
          </h3>
          <p className="text-xs text-slate-400 mb-3">If sold consistently exceeds purchased, you are under-buying (and vice versa).</p>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={pvs}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 10 }} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#334155", opacity: 0.3 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="purchased" fill="#F59E0B" name="Purchased" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="sold" stroke="#10B981" strokeWidth={2} name="Sold" dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div data-testid="purchase-planner-table" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
        <h3 className="text-lg font-semibold mb-1 flex items-center justify-between" style={{ fontFamily: "Outfit, sans-serif" }}>
          <span>Purchase Planner</span>
          <ExportBtn onCSV={() => exportCSV(data.planner, "purchase-planner")} />
        </h3>
        <p className="text-xs text-slate-400 mb-3">Suggested buy = 1.5 months of average sales (last 90 days) minus current stock. Red cover = under 1 month of stock left.</p>
        <div className="overflow-auto max-h-[480px]">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="sticky top-0 bg-[#1E293B]">
              <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-[#334155]">
                <th className="pb-2 pr-3">Model</th>
                <th className="pb-2 pr-3 text-right">In Stock</th>
                <th className="pb-2 pr-3 text-right">Avg Stock Age</th>
                <th className="pb-2 pr-3 text-right">Sold 30d</th>
                <th className="pb-2 pr-3 text-right">Sold 90d</th>
                <th className="pb-2 pr-3 text-right">Sold 12m</th>
                <th className="pb-2 pr-3 text-right">Avg / Month</th>
                <th className="pb-2 pr-3 text-right">Stock Cover</th>
                <th className="pb-2 pr-3 text-right">Bought 90d</th>
                <th className="pb-2 pr-3 text-right">Last Purchase</th>
                <th className="pb-2 text-right">Suggested Buy</th>
              </tr>
            </thead>
            <tbody>
              {data.planner.map((p, i) => (
                <tr key={p.model} data-testid={`planner-row-${i}`} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors">
                  <td className="py-2 pr-3 text-slate-200">{p.model} {p.code && <span className="text-slate-500 text-xs font-mono">({p.code})</span>}</td>
                  <td className="py-2 pr-3 text-right font-mono text-amber-400">{p.stock}</td>
                  <td className={`py-2 pr-3 text-right font-mono ${p.avg_stock_age > 90 ? "text-rose-400" : p.avg_stock_age > 60 ? "text-orange-400" : "text-slate-300"}`}>{p.stock > 0 ? `${p.avg_stock_age}d` : "—"}</td>
                  <td className="py-2 pr-3 text-right font-mono">{p.sold_30d}</td>
                  <td className="py-2 pr-3 text-right font-mono">{p.sold_90d}</td>
                  <td className="py-2 pr-3 text-right font-mono text-slate-400">{p.sold_12m}</td>
                  <td className="py-2 pr-3 text-right font-mono">{p.avg_monthly_sale}</td>
                  <td className={`py-2 pr-3 text-right font-mono ${p.months_cover != null && p.months_cover < 1 ? "text-rose-400 font-bold" : "text-slate-300"}`}>
                    {p.months_cover != null ? `${p.months_cover} mo` : "—"}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-cyan-400">{p.purchased_90d}</td>
                  <td className="py-2 pr-3 text-right font-mono text-xs text-slate-400">{p.last_purchase || "—"}</td>
                  <td className="py-2 text-right">
                    {p.suggested_buy > 0
                      ? <Badge className="bg-blue-600 text-white font-mono">{p.suggested_buy}</Badge>
                      : <span className="text-slate-600 font-mono">0</span>}
                  </td>
                </tr>
              ))}
              <tr data-testid="planner-total-row" className="border-t-2 border-slate-600 font-bold">
                <td className="py-2.5 pr-3 text-slate-100">TOTAL</td>
                <td className="py-2.5 pr-3 text-right font-mono text-amber-400">{data.planner.reduce((t, p) => t + p.stock, 0)}</td>
                <td></td>
                <td className="py-2.5 pr-3 text-right font-mono">{data.planner.reduce((t, p) => t + p.sold_30d, 0)}</td>
                <td className="py-2.5 pr-3 text-right font-mono">{data.planner.reduce((t, p) => t + p.sold_90d, 0)}</td>
                <td className="py-2.5 pr-3 text-right font-mono">{data.planner.reduce((t, p) => t + p.sold_12m, 0)}</td>
                <td colSpan={2}></td>
                <td className="py-2.5 pr-3 text-right font-mono text-cyan-400">{data.planner.reduce((t, p) => t + p.purchased_90d, 0)}</td>
                <td></td>
                <td className="py-2.5 text-right font-mono text-blue-400">{data.planner.reduce((t, p) => t + p.suggested_buy, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
